import React, { useState } from "react";
import { toast } from "sonner";
import { Check, X, UserCheck, UserMinus, UserPlus, ListTodo, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useConfirm } from "../../context/ConfirmContext";
import { TeamInvitePanel } from "./TeamInvitePanel";

const inputCls = "bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 outline-none px-3 py-2 text-sm";
const STATUS = { todo: { bg: "#56626A1A", c: "#56626A", l: "To Do" }, in_progress: { bg: "#0844381A", c: "#084438", l: "In Progress" }, done: { bg: "#17A88A1A", c: "#17A88A", l: "Done" } };
const H = ({ icon: Icon, children }) => <h3 className="font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--sj-text-3)" }}><Icon className="h-4 w-4" /> {children}</h3>;
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

const Applications = ({ team, reload }) => {
  const decide = (id, action) => api.post(`/team-applications/${id}/${action}`)
    .then(() => { toast.success(action === "approve" ? "Approved — member added" : "Application declined"); reload(); }).catch(err);
  if (!team.applications.length) return <p className="text-sm" style={{ color: "var(--sj-text-3)" }} data-testid={`my-team-no-apps-${team.id}`}>No applications waiting.</p>;
  return (
    <div className="space-y-2">
      {team.applications.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-3 bg-white rounded-xl p-3 border" style={{ borderColor: "var(--sj-border)" }} data-testid={`my-team-app-${a.id}`}>
          <div className="min-w-0">
            <div className="font-semibold text-sm">{a.member_name}</div>
            {a.reason && <p className="text-xs italic line-clamp-2" style={{ color: "var(--sj-text-3)" }}>"{a.reason}"</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button data-testid={`my-team-approve-${a.id}`} onClick={() => decide(a.id, "approve")} title="Accept" className="h-9 w-9 rounded-full flex items-center justify-center text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }}><Check className="h-4 w-4" /></button>
            <button data-testid={`my-team-reject-${a.id}`} onClick={() => decide(a.id, "reject")} title="Decline" className="h-9 w-9 rounded-full flex items-center justify-center border hover:bg-red-50" style={{ borderColor: "#DC6A5C", color: "#C0392B" }}><X className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

const Members = ({ team, reload }) => {
  const confirm = useConfirm();
  const remove = async (m) => {
    if (!(await confirm({ title: `Remove ${m.name}?`, description: `${m.name} will be taken off ${team.name}.`, confirmLabel: "Remove", tone: "danger" }))) return;
    api.delete(`/portal/my-teams/${team.id}/members/${m.id}`).then(() => { toast.success(`${m.name} removed`); reload(); }).catch(err);
  };
  if (!team.members.length) return <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No members yet.</p>;
  return (
    <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
      {team.members.map((m) => (
        <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ background: "var(--sj-bg)" }} data-testid={`my-team-member-${m.id}`}>
          <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0" style={{ background: "var(--sj-primary)" }}>{(m.name || "?")[0]}</div>
          <span className="text-sm font-medium flex-1 min-w-0 truncate">{m.name}{m.is_me && " (you)"}{m.is_assistant && <span className="ml-1.5 text-[10px] font-bold uppercase" style={{ color: "#8a6a00" }}>Assistant Head</span>}</span>
          {!m.is_me && <button data-testid={`my-team-remove-${m.id}`} onClick={() => remove(m)} title="Remove from team" className="p-1.5 rounded-full hover:bg-red-50" style={{ color: "#C0392B" }}><UserMinus className="h-4 w-4" /></button>}
        </div>
      ))}
    </div>
  );
};

const Tasks = ({ team, reload }) => {
  const [title, setTitle] = useState("");
  const add = () => title.trim() && api.post("/leader/tasks", { title: title.trim(), team_id: team.id, priority: "medium", status: "todo" }).then(() => { setTitle(""); reload(); }).catch(err);
  const cycle = (t) => api.put(`/leader/tasks/${t.id}`, { ...t, status: t.status === "todo" ? "in_progress" : t.status === "in_progress" ? "done" : "todo" }).then(reload).catch(err);
  const del = (id) => api.delete(`/leader/tasks/${id}`).then(reload).catch(err);
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input data-testid={`my-team-task-input-${team.id}`} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a task..." className={`${inputCls} flex-1 min-w-0`} />
        <button data-testid={`my-team-add-task-${team.id}`} onClick={add} className="rounded-full h-9 w-9 shrink-0 flex items-center justify-center text-white" style={{ background: "var(--sj-primary)" }}><Plus className="h-4 w-4" /></button>
      </div>
      {!team.tasks.length && <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No tasks yet.</p>}
      <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
        {team.tasks.map((tk) => { const s = STATUS[tk.status] || STATUS.todo; return (
          <div key={tk.id} className="flex items-center gap-2 p-2 rounded-lg border" style={{ borderColor: "var(--sj-border)" }} data-testid={`my-team-task-${tk.id}`}>
            <span className={`flex-1 min-w-0 text-sm ${tk.status === "done" ? "line-through opacity-60" : ""}`}>{tk.title}</span>
            <button data-testid={`my-team-task-status-${tk.id}`} onClick={() => cycle(tk)} className="rounded-full px-2.5 py-0.5 text-xs font-semibold shrink-0" style={{ background: s.bg, color: s.c }}>{s.l}</button>
            <button data-testid={`my-team-task-delete-${tk.id}`} onClick={() => del(tk.id)} className="p-1 rounded hover:bg-[#E3F1EC] shrink-0" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>); })}
      </div>
    </div>
  );
};

export const TeamManagePanel = ({ team, reload }) => (
  <div className="mt-5 space-y-6" data-testid={`my-team-manage-${team.id}`}>
    <div className="rounded-xl p-4" style={{ background: "var(--sj-peach)" }}>
      <H icon={UserCheck}>Applications to join ({team.applications.length})</H>
      <Applications team={team} reload={reload} />
    </div>
    <div><H icon={UserPlus}>Invite members</H><TeamInvitePanel team={team} reload={reload} /></div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div><H icon={UserCheck}>Members ({team.members.length})</H><Members team={team} reload={reload} /></div>
      <div><H icon={ListTodo}>Team tasks</H><Tasks team={team} reload={reload} /></div>
    </div>
  </div>
);
